import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import { createBasicSelector } from 'util/redux'
import { emptyObj } from 'util/objects'
import { groupByToMap } from 'util/arrays'

export const selectIntegrationData = state =>
  state.integrations.thirdPartyData.shopify

export const selectProducts = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.products
)

export const selectShopInfo = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.shop || {}
)

export const selectCurrencyCode = createBasicSelector(
  selectShopInfo,
  shop => shop.currencyCode
)

export const selectContactEmail = createBasicSelector(
  selectShopInfo,
  shop => shop.contactEmail
)

export const selectShopIsLoading = createBasicSelector(
  selectShopInfo,
  shop => shop.isLoading
)

export const selectDraftOrders = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.draftOrders
)

export const selectDraftOrdersById = createBasicSelector(
  selectDraftOrders,
  integrationData => integrationData.byId || {}
)

export const selectDraftOrdersByCustomerId = createBasicSelector(
  selectDraftOrders,
  draftOrders => draftOrders.byCustomerId || {}
)

export const selectDraftOrderForCustomerId = createCachedSelector(
  selectDraftOrdersByCustomerId,
  selectDraftOrdersById,
  (state, { customerId }) => customerId,
  (byCustomerId, byId, customerId) => {
    const draftOrderId = byCustomerId[customerId]
    if (!draftOrderId) return null

    return byId[draftOrderId]
  }
)((state, { customerId }) => customerId || 'unknown')

export const selectProductsByQuery = createBasicSelector(
  selectProducts,
  products => products.byQuery
)

export const selectProductsById = createBasicSelector(
  selectProducts,
  products => products.byId
)

export const selectProductsForQuery = createCachedSelector(
  selectProductsByQuery,
  selectProductsById,
  (state, { query }) => query,
  (byQuery, byId, query) => {
    const productIds = byQuery[query] || []
    return productIds.map(productId => byId[productId])
  }
)((state, { query }) => query || 'unknown')

export const selectOrders = state =>
  state.integrations.thirdPartyData.shopify.orders

export const selectOrdersLoading = createBasicSelector(
  selectOrders,
  orders => orders.isLoading
)

export const selectOrdersByCustomerId = createBasicSelector(
  selectOrders,
  orders => orders.byCustomerId || emptyObj
)

export const selectEvents = state =>
  state.integrations.thirdPartyData.shopify.events

export const selectEventsLoading = createBasicSelector(
  selectEvents,
  events => events.isLoading
)

export const selectIsLoading = createBasicSelector(
  selectOrdersLoading,
  selectEventsLoading,
  selectShopIsLoading,
  (ordersLoading, eventsLoading, shopLoading) =>
    ordersLoading || eventsLoading || shopLoading
)

export const selectEventsByCustomerId = createBasicSelector(
  selectEvents,
  events => events.byCustomerId || emptyObj
)

export const selectEventsForCustomerId = createCachedSelector(
  selectEventsByCustomerId,
  (state, { customerId }) => customerId,
  (byCustomerId, customerId) => {
    const forCustomerId = byCustomerId[customerId]
    return forCustomerId
  }
)((state, { customerId }) => customerId || 'unknown')

export const selectEventsForCustomerIdGroupedByDay = createCachedSelector(
  selectEventsByCustomerId,
  (state, { customerId }) => customerId,
  (byCustomerId, customerId) => {
    const forCustomerId = byCustomerId[customerId] || []
    return groupByToMap(forCustomerId, ({ date }) => date)
  }
)((state, { customerId }) => customerId || 'unknown')

export const selectOrdersForCustomerId = createCachedSelector(
  selectOrdersByCustomerId,
  (state, { customerId }) => customerId,
  (byCustomerId, customerId) => {
    const forCustomerId = byCustomerId[customerId] || []
    return forCustomerId
  }
)((state, { customerId }) => customerId || 'unknown')

export const selectOrdersById = createSelector(
  selectOrdersByCustomerId,
  byCustomerId => {
    const byId = {}
    Object.keys(byCustomerId).forEach(customerId => {
      Object.values(byCustomerId[customerId]).forEach(order => {
        const shortId = order.id.split('/').pop()
        byId[shortId] = order
      })
    })
    return byId
  }
)

export const selectOrderForId = createCachedSelector(
  selectOrdersById,
  (state, { orderId }) => orderId,
  (byId, orderId) => {
    return byId[orderId]
  }
)((state, { orderId }) => orderId || 'unknown')
